import { useUserContext } from '@lemonade-hq/nuiza';
import { useNavContext } from '@lemonade-hq/uniclient-app';
import type {
  HeaderAction,
  HeaderActionType,
  HeaderMenuProps,
  UniclientAction,
  UniclientCoInsurance,
} from '@lemonade-hq/uniclient-chat';
import { PredefinedMenuOptionType } from '@lemonade-hq/uniclient-chat';
import { Flex, IconButton, Logo, spacing, Text } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import React, { forwardRef } from 'react';
import type { HeaderPredefinedMenuActions } from '../hooks/useWizardMenu';
import { shouldShowNativeAction } from '../hooks/useWizardMenu';
import { useAutoTriggerActionContext } from '../providers/AutoTriggerActionProvider';
import { CoInsurance } from './CoInsurance';
import { logoWithCoInsurance } from './CoInsurance.css';
import { FreeTextInputButton } from './components/freeTextInput/FreeTextInputButton';
import * as styles from './WizardHeader.css';
import { WizardHeaderMenu } from './WizardHeaderMenu';
import { WizardLink } from './WizardLink';
import { LAYOUT_PADDING_FROM_THE_SIDE } from './WizardMain.css';

const HEADER_ICON_BUTTON_SIZE = 'lg';

interface WizardHeaderActionProps extends Omit<WizardHeaderActionsProps, 'actions'> {
  readonly action: HeaderAction | HeaderMenuProps | PredefinedMenuOptionType.FreeTextInput;
}

const WizardHeaderAction = forwardRef<HTMLDivElement, WizardHeaderActionProps>((props, ref) => {
  const { action, predefinedActions, performAction } = props;
  let component = null;

  const { registerAutoTriggerAction } = useAutoTriggerActionContext();

  if (action === PredefinedMenuOptionType.FreeTextInput) {
    component = (
      <FreeTextInputButton
        setShowFreeTextInput={props.setShowFreeTextInput}
        showFreeTextInput={props.showFreeTextInput}
        size={HEADER_ICON_BUTTON_SIZE}
      />
    );
  } else if ('subActions' in action) {
    component = (
      <WizardHeaderMenu
        menuAction={action}
        menuIconButtonSize={HEADER_ICON_BUTTON_SIZE}
        performAction={performAction}
        predefinedActions={predefinedActions}
      />
    );
  } else if ('content' in action && action.content != null) {
    registerAutoTriggerAction(action, performAction);
    component = (
      <Text
        className={styles.textAction}
        color="$textSecondary"
        cursor={action.disabledOnDesktop ? 'default' : 'pointer'}
        onClick={action.disabledOnDesktop ? undefined : () => void performAction(action)}
        type="bodySmall"
        width="max-content"
      >
        {action.content}
      </Text>
    );
  } else if ('disabledOnDesktop' in action && action.disabledOnDesktop) {
    return null;
  } else if ('icon' in action) {
    registerAutoTriggerAction(action, performAction);
    component = (
      <IconButton icon={action.icon} onClick={() => void performAction(action)} size={HEADER_ICON_BUTTON_SIZE} />
    );
  }

  return (
    <Flex
      animate={{ opacity: 1 }}
      as={motion.div}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      layout="position"
      ref={ref}
    >
      {component}
    </Flex>
  );
});

interface WizardHeaderLogoProps {
  readonly coInsurance?: UniclientCoInsurance;
  readonly logoAction?: HeaderAction | PredefinedMenuOptionType.Exit | PredefinedMenuOptionType.ImmediateExit | null;
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
  readonly hideLogo?: boolean;
}

export const WizardHeaderLogo: React.FC<WizardHeaderLogoProps> = ({
  coInsurance,
  logoAction,
  performAction,
  hideLogo,
}) => {
  const {
    nav: { homeUrl, consumerUrl },
  } = useNavContext();

  const { user } = useUserContext();
  const isLogged = user != null;

  const showLogoAction =
    shouldShowNativeAction(logoAction) &&
    logoAction !== PredefinedMenuOptionType.Exit &&
    logoAction !== PredefinedMenuOptionType.ImmediateExit;

  return (
    <Flex
      alignItems="center"
      className={styles.header}
      flexDirection="row"
      gap={`calc(${spacing.s24} - ${styles.LOGO_PADDING})`}
      justifyContent="flex-start"
      pl={{
        lg: `calc(${LAYOUT_PADDING_FROM_THE_SIDE.md} - ${styles.LOGO_PADDING})`,
      }}
    >
      <AnimatePresence mode="popLayout">
        {showLogoAction && <WizardHeaderAction action={logoAction} performAction={performAction} />}
      </AnimatePresence>
      <Flex alignItems="center" as={motion.div} flexDirection="row" gap={spacing.s06} layout="position">
        <AnimatePresence>
          {!hideLogo && (
            <Flex
              animate={{ opacity: 1 }}
              as={motion.div}
              exit={{ opacity: 0 }}
              flexDirection="row"
              gap={spacing.s04}
              initial={{ opacity: 0 }}
            >
              <WizardLink
                className={styles.logoContainer({ noPadding: coInsurance?.position === 'top' })}
                href={isLogged ? consumerUrl : homeUrl}
              >
                {coInsurance != null ? <Logo className={logoWithCoInsurance} /> : <Logo />}
              </WizardLink>
              {coInsurance != null && coInsurance.position === 'top' && (
                <CoInsurance coInsurance={coInsurance} isMobile={false} />
              )}
            </Flex>
          )}
        </AnimatePresence>
      </Flex>
    </Flex>
  );
};

interface WizardHeaderActionsProps {
  readonly actions?: (HeaderActionType | PredefinedMenuOptionType.FreeTextInput)[];
  readonly predefinedActions?: HeaderPredefinedMenuActions;
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
  readonly showFreeTextInput?: boolean;
  readonly setShowFreeTextInput?: (showFreeTextInput: boolean) => void;
}

export const WizardHeaderActions: React.FC<WizardHeaderActionsProps> = ({
  actions = [],
  predefinedActions,
  performAction,
  showFreeTextInput,
  setShowFreeTextInput,
}) => {
  return (
    <Flex
      alignItems="center"
      className={styles.header}
      flexDirection="row"
      gap={styles.HEADER_ITEMS_GAP}
      justifyContent="flex-end"
      pr={LAYOUT_PADDING_FROM_THE_SIDE}
    >
      <AnimatePresence>
        {actions.map(action => {
          const key =
            action === PredefinedMenuOptionType.FreeTextInput
              ? 'freeTextInput'
              : typeof action === 'object' && 'subActions' in action
                ? 'menu'
                : typeof action === 'object' && 'content' in action
                  ? action.content ?? action.icon
                  : typeof action === 'object'
                    ? action.icon
                    : String(action);

          return (
            <WizardHeaderAction
              action={action}
              key={key}
              performAction={performAction}
              predefinedActions={predefinedActions}
              setShowFreeTextInput={setShowFreeTextInput}
              showFreeTextInput={showFreeTextInput}
            />
          );
        })}
      </AnimatePresence>
    </Flex>
  );
};
